import * as io from "io-ts";

import { css } from "@linaria/core";

import { Filter3Line } from "../IconV2/react";
import createGlobalState from "../lib/createGlobalState";
import { spacings } from "../Theme";
import React, {
  createContext,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
  type PropsWithChildren,
} from "react";
import { createPortal } from "react-dom";
import Reset from "../Filter/Reset";
import FilterSelect from "../Filter/FilterSelect";
import ButtonIconV2, { ButtonIconMarker } from "../ButtonIconV2";

const TableFilterValues = io.record(io.string, io.array(io.union([io.string, io.null])));
type TableFilterValues = io.TypeOf<typeof TableFilterValues>;

const portalId = window.crypto.randomUUID();

const useTableFilterValues = createGlobalState({
  key: "table-filter",
  storage: "session",
  type: TableFilterValues,
  scoped: true,
});

export function useTableFilterResults<T, C extends string>({
  items,
  predicates,
  scope,
}: {
  items: T[];
  predicates: Record<C, (filterValues: TableFilterValues[C], item: T) => boolean>;
  scope: string;
}) {
  const [filterValues] = useTableFilterValues({ scope });
  const results = useMemo(() => {
    if (!filterValues) {
      return items;
    }
    return items.filter(item =>
      Object.entries(filterValues).every(
        ([key, values]) => values.length === 0 || (predicates[key as C]?.(values, item) ?? false),
      ),
    );
  }, [items, predicates, filterValues]);
  return useDeferredValue(results);
}

const TableFilterContext = createContext<{
  scope: string;
  getValues: (key: string) => TableFilterValues[typeof key];
  setValues: (key: string) => (values: TableFilterValues[typeof key]) => void;
}>({
  scope: window.crypto.randomUUID(),
  getValues: () => [null],
  setValues: () => () => {},
});

export function TableFilter<T extends TableFilterValues>({
  scope,
  defaultValues,
  children,
}: PropsWithChildren<{
  scope: string;
  defaultValues: T;
}>) {
  const [filterValues, setFilterValues] = useTableFilterValues({
    scope,
    defaultValue: defaultValues,
  });
  const [opened, setOpened] = useState<boolean>(false);

  const getValues = (key: string) => filterValues[key] ?? [];
  const setValues = (key: string) => (values: TableFilterValues[typeof key]) => {
    const newFilterValues = { ...filterValues, [key]: values };
    setFilterValues(newFilterValues);
  };

  const portalRoot = useDeferredValue(document.getElementById(portalId));

  const count = Object.values(filterValues).filter(values => values.length > 0).length;

  useEffect(() => {
    if (JSON.stringify(filterValues) === JSON.stringify(defaultValues)) {
      setFilterValues(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TableFilterContext.Provider value={{ getValues, setValues, scope }}>
      <ButtonIconV2
        className={tableFilterButton}
        checked={opened}
        onClick={() => setOpened(o => !o)}
      >
        {count > 0 && <ButtonIconMarker />}
        <Filter3Line size="m" />
      </ButtonIconV2>
      {portalRoot && opened && createPortal(children, portalRoot)}
    </TableFilterContext.Provider>
  );
}

export function TableFilterSelect<K extends string>({
  filter,
  options,
  title,
  filtered,
  width,
}: { filter: K } & Pick<
  ComponentProps<typeof FilterSelect>,
  "title" | "options" | "filtered" | "width"
>) {
  const tableFilterSettingsContext = useContext(TableFilterContext);

  if (!tableFilterSettingsContext || options.length < 1) {
    return null;
  }

  return (
    <FilterSelect
      filtered={filtered}
      title={title}
      options={options}
      values={tableFilterSettingsContext.getValues(filter) ?? []}
      setValues={tableFilterSettingsContext.setValues(filter)}
      width={width}
    />
  );
}

export const TableFilterReset = ({
  defaultValues,
}: {
  defaultValues: ComponentProps<typeof Reset>["defaultFilterValues"];
}) => {
  const { scope } = useContext(TableFilterContext);
  const [filterValues, setFilterValues] = useTableFilterValues({
    scope,
    defaultValue: defaultValues,
  });
  return (
    <Reset
      icon
      filterValues={filterValues}
      setFilterValues={setFilterValues}
      defaultFilterValues={defaultValues}
    />
  );
};

export const TableFilterList = () => <div id={portalId} className={tableFilterList} />;

const tableFilterList = css`
  align-items: center;
  display: flex;
  gap: ${spacings.xs};
  width: 100%;

  &:empty {
    display: none;
  }
`;

const tableFilterButton = css`
  width: 22px;
  height: 22px;
`;
